const METRICS_ENABLED = import.meta.env.VITE_METRICS_ENABLED === 'true'
const VMAGENT_URL = import.meta.env.VITE_METRICS_VMAGENT_URL || '/api/vm/api/v1/import/prometheus'
const FLUSH_INTERVAL_MS = Number(import.meta.env.VITE_METRICS_FLUSH_INTERVAL) || 30_000
const JOB = import.meta.env.VITE_METRICS_JOB || 'webchat'

export interface MetricLabels {
  [key: string]: string
}

interface MetricEntry {
  name: string
  labels: MetricLabels
  value: number
}

function buildKey(name: string, labels: MetricLabels): string {
  const sorted = Object.entries(labels)
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([k, v]) => `${k}=${v}`)
    .join(',')
  return `${name}{${sorted}}`
}

function labelsToString(labels: MetricLabels): string {
  return Object.entries(labels)
    .map(([k, v]) => `${k}="${v.replace(/\\/g, '\\\\').replace(/"/g, '\\"')}"`)
    .join(',')
}

function buildPayload(store: Map<string, MetricEntry>, job: string, instance: string): string {
  const ts = Date.now()
  return [...store.values()]
    .map(({ name, labels, value }) => {
      const allLabels: MetricLabels = { job, instance, ...labels }
      return `${name}{${labelsToString(allLabels)}} ${value} ${ts}`
    })
    .join('\n')
}

class MetricsCollector {
  private store = new Map<string, MetricEntry>()
  private readonly sessionId: string
  private flushTimer: ReturnType<typeof setInterval> | null = null

  constructor() {
    this.sessionId = crypto.randomUUID()
  }

  /**
   * Инкрементирует счётчик. Значения накапливаются в рамках сессии.
   */
  inc(name: string, labels: MetricLabels = {}, delta = 1): void {
    if (!METRICS_ENABLED) return
    const key = buildKey(name, labels)
    const entry = this.store.get(key)
    if (entry) {
      entry.value += delta
    } else {
      this.store.set(key, { name, labels, value: delta })
    }
  }

  /**
   * Устанавливает текущее значение gauge (перезаписывает).
   */
  gauge(name: string, labels: MetricLabels = {}, value: number): void {
    if (!METRICS_ENABLED) return
    const key = buildKey(name, labels)
    this.store.set(key, { name, labels, value })
  }

  /**
   * Накапливает наблюдение для histogram-like метрики.
   * Отправляет _sum и _count — достаточно для avg/rate в Grafana.
   */
  observe(name: string, value: number, labels: MetricLabels = {}): void {
    if (!METRICS_ENABLED) return
    this.inc(`${name}_sum`, labels, value)
    this.inc(`${name}_count`, labels, 1)
  }

  startFlushInterval(): void {
    if (!METRICS_ENABLED) return
    this.flushTimer = setInterval(() => void this.flush(), FLUSH_INTERVAL_MS)
  }

  stopFlushInterval(): void {
    if (this.flushTimer !== null) {
      clearInterval(this.flushTimer)
      this.flushTimer = null
    }
  }

  async flush(): Promise<void> {
    if (!METRICS_ENABLED || this.store.size === 0) return
    const body = buildPayload(this.store, JOB, this.sessionId)
    try {
      await fetch(VMAGENT_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'text/plain' },
        body,
      })
    } catch {
      // silent — метрики не должны ломать приложение
    }
  }

  /**
   * Синхронный flush через sendBeacon при закрытии вкладки.
   */
  flushBeacon(): void {
    if (!METRICS_ENABLED || this.store.size === 0) return
    const body = buildPayload(this.store, JOB, this.sessionId)
    navigator.sendBeacon(VMAGENT_URL, new Blob([body], { type: 'text/plain' }))
  }
}

let collector: MetricsCollector | null = null

export function useMetrics(): MetricsCollector {
  if (!collector) {
    collector = new MetricsCollector()
  }
  return collector
}

export type { MetricsCollector }
